import { Button, Input } from '@roger/ui-components';
import { Loader2, Scissors } from 'lucide-react';
import PageEditor from './PageEditor';
import type { FaxItemEditor } from './useFaxItemEditor';

interface FaxPagesSectionProps {
  editor: FaxItemEditor;
  /** Read-only, such as a fax already filed to a chart. */
  disabled?: boolean;
  onSplit: () => void;
}

/** The pages of the fax, with the bar for splitting ticked pages out. */
export default function FaxPagesSection({
  editor,
  disabled = false,
  onSplit,
}: FaxPagesSectionProps) {
  const { splitKeys, taken, kept, saving } = editor;

  if (editor.preparing) {
    return (
      <div className='flex justify-center py-10'>
        <Loader2 className='h-6 w-6 animate-spin' />
      </div>
    );
  }

  return (
    <div className='flex min-w-0 flex-col gap-3'>
      {splitKeys.size > 0 && (
        <div className='flex shrink-0 flex-col gap-2 rounded border bg-muted/40 p-3 sm:flex-row sm:items-center'>
          <span className='whitespace-nowrap text-sm'>
            {taken.length === 1
              ? '1 page selected'
              : `${taken.length} pages selected`}
          </span>
          <Input
            aria-label='Name for the new fax'
            placeholder='What the new fax is'
            className='sm:flex-1'
            value={editor.splitName}
            disabled={saving || disabled}
            onChange={event => editor.setSplitName(event.target.value)}
          />
          <Button
            variant='outline'
            className='whitespace-nowrap'
            disabled={saving || disabled || !editor.canSplit}
            onClick={onSplit}
          >
            <Scissors className='mr-2 h-4 w-4' />
            Split into a new fax
          </Button>
        </div>
      )}
      {kept.length === 0 && splitKeys.size > 0 && (
        <p className='shrink-0 text-sm text-muted-foreground'>
          Leave at least one page behind, or there is nothing left to split
          from.
        </p>
      )}
      <PageEditor
        sources={editor.sources}
        pages={editor.pages}
        onChange={editor.setPages}
        onSourceLoaded={editor.onSourceLoaded}
        disabled={saving || disabled}
        splitKeys={splitKeys}
        onToggleSplit={disabled ? undefined : editor.toggleSplit}
      />
    </div>
  );
}
